import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress    
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminConfigAlerts.module.css';
import _ from 'lodash';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';

const AdminConfigAlerts = () => {
  const [searchParams] = useSearchParams();
  const [tractor, setTractor] = useState({});
  const [alerts, setAlerts] = useState({});
  const [fetchedAlerts, setFetchedAlerts] = useState({});
  const [loading, setLoading] = useState(true);

  const tractorId = searchParams.get('tractorId');

  // Fetch tractor for defaultTargetApplicationRateGalAcre
  useEffect(() => {
    const fetchTractor = async () => {
      setLoading(true);
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/all`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` },
          }
        );
        if (response.ok) {
          const tractors = await response.json();
          const foundTractor = tractors.find(t => t.id === parseInt(tractorId));
          setTractor(foundTractor);
        }
      } catch (error) {
        toast.error('Failed to load tractor');
      }
      setLoading(false);
    };
    if (tractorId) fetchTractor();
  }, [tractorId]);

  useEffect(() => {
    if (!tractorId || !tractor || !tractor.defaultTargetApplicationRateGalAcre) return;

    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/alerts?tractorId=${tractorId}`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) throw new Error('Failed to fetch alerts');
        const data = await response.json();
        const rate = Number(tractor.defaultTargetApplicationRateGalAcre) || 0;
        const targetPressurePsi = Number(tractor.targetPressurePsi ?? tractor.targetApplicationPsi) || 0;
        const spotConfig = JSON.parse(data.appRateSpotSprayAlert.configJson);
        const broadcastConfig = JSON.parse(data.appRateBroadcastAlert.configJson);
        const pressureConfig = JSON.parse(data.pressureAlert.configJson);
        
        const newAlerts = {
          spot: {
            id: data.appRateSpotSprayAlert.id,
            enabled: data.appRateSpotSprayAlert.enabled,
            triggerAfterMinutes: data.appRateSpotSprayAlert.triggerAfterMinutes,
            cooldownMinutes: data.appRateSpotSprayAlert.cooldownMinutes,
            lastTriggeredUtc: data.appRateSpotSprayAlert.lastTriggeredUtc,
            minEnabled: spotConfig.MinThresholdEnabled,
            minPercent: spotConfig.MinThresholdPercent,
            maxEnabled: spotConfig.MaxThresholdEnabled,
            maxPercent: spotConfig.MaxThresholdPercent,
            minGalAcre: rate ? ((spotConfig.MinThresholdPercent / 100) * rate).toFixed(2) : '',
            maxGalAcre: rate ? ((spotConfig.MaxThresholdPercent / 100) * rate).toFixed(2) : ''
          },
          broadcast: {
            id: data.appRateBroadcastAlert.id,
            enabled: data.appRateBroadcastAlert.enabled,
            triggerAfterMinutes: data.appRateBroadcastAlert.triggerAfterMinutes,
            cooldownMinutes: data.appRateBroadcastAlert.cooldownMinutes,
            lastTriggeredUtc: data.appRateBroadcastAlert.lastTriggeredUtc,
            minEnabled: broadcastConfig.MinThresholdEnabled,
            minPercent: broadcastConfig.MinThresholdPercent,
            maxEnabled: broadcastConfig.MaxThresholdEnabled,
            maxPercent: broadcastConfig.MaxThresholdPercent,
            minGalAcre: rate ? ((broadcastConfig.MinThresholdPercent / 100) * rate).toFixed(2) : '',
            maxGalAcre: rate ? ((broadcastConfig.MaxThresholdPercent / 100) * rate).toFixed(2) : ''
          },
          pressure: {
            id: data.pressureAlert.id,
            enabled: data.pressureAlert.enabled,
            triggerAfterMinutes: data.pressureAlert.triggerAfterMinutes,
            cooldownMinutes: data.pressureAlert.cooldownMinutes,
            lastTriggeredUtc: data.pressureAlert.lastTriggeredUtc,
            minEnabled: pressureConfig.MinThresholdEnabled,
            minPercent: pressureConfig.MinThresholdPercent,
            maxEnabled: pressureConfig.MaxThresholdEnabled,
            maxPercent: pressureConfig.MaxThresholdPercent,
            minPressurePsi: targetPressurePsi ? ((pressureConfig.MinThresholdPercent / 100) * targetPressurePsi).toFixed(2) : '',
            maxPressurePsi: targetPressurePsi ? ((pressureConfig.MaxThresholdPercent / 100) * targetPressurePsi).toFixed(2) : ''
          }
        };
        setFetchedAlerts(newAlerts);
        setAlerts(_.cloneDeep(newAlerts));
      } catch (error) {

        toast.error('Failed to load alerts');
      }
      setLoading(false);
    };

    fetchAlerts();
  }, [tractorId, tractor]);

  // Handlers for alert state
  const handleToggle = (type, field) => (event) => {
    setAlerts((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: event.target.checked,
      },
    }));
  };

  const handleInput = (type, field) => (event) => {
    let value = event.target.value;
    // Only allow numbers and decimals
    if (value === '' || /^[0-9.]+$/.test(value)) {
      setAlerts((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          [field]: value,
        },
      }));
    }
  };

  // Sync percent <-> value per alert
  useEffect(() => {
    const syncAlert = (key, rate, minField, maxField) => {
      if (!alerts[key]) return;

      const minPercent = parseFloat(alerts[key].minPercent);
      const maxPercent = parseFloat(alerts[key].maxPercent);
      const minValue = parseFloat(alerts[key][minField]);
      const maxValue = parseFloat(alerts[key][maxField]);

      if (alerts[key].minPercent !== '' && document.activeElement?.name === `${key}-minPercent`) {
        setAlerts((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            [minField]: !isNaN(minPercent) && !isNaN(rate) ? (minPercent / 100 * rate).toFixed(2) : '',
          },
        }));
      }

      if (alerts[key].maxPercent !== '' && document.activeElement?.name === `${key}-maxPercent`) {
        setAlerts((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            [maxField]: !isNaN(maxPercent) && !isNaN(rate) ? (maxPercent / 100 * rate).toFixed(2) : '',
          },
        }));
      }

      if (alerts[key][minField] !== '' && document.activeElement?.name === `${key}-${minField}`) {
        setAlerts((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            minPercent: !isNaN(minValue) && rate ? ((minValue / rate) * 100).toFixed(2) : '',
          },
        }));
      }

      if (alerts[key][maxField] !== '' && document.activeElement?.name === `${key}-${maxField}`) {
        setAlerts((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            maxPercent: !isNaN(maxValue) && rate ? ((maxValue / rate) * 100).toFixed(2) : '',
          },
        }));
      }
    };

    const appRate = Number(tractor.defaultTargetApplicationRateGalAcre) || 0;
    const pressureRate = Number(tractor.targetPressurePsi ?? tractor.targetApplicationPsi) || 0;

    syncAlert('spot', appRate, 'minGalAcre', 'maxGalAcre');
    syncAlert('broadcast', appRate, 'minGalAcre', 'maxGalAcre');
    syncAlert('pressure', pressureRate, 'minPressurePsi', 'maxPressurePsi');
    // eslint-disable-next-line
  }, [
    alerts.spot?.minPercent,
    alerts.spot?.maxPercent,
    alerts.spot?.minGalAcre,
    alerts.spot?.maxGalAcre,
    alerts.broadcast?.minPercent,
    alerts.broadcast?.maxPercent,
    alerts.broadcast?.minGalAcre,
    alerts.broadcast?.maxGalAcre,
    alerts.pressure?.minPercent,
    alerts.pressure?.maxPercent,
    alerts.pressure?.minPressurePsi,
    alerts.pressure?.maxPressurePsi,
    tractor.defaultTargetApplicationRateGalAcre,
    tractor.targetPressurePsi,
    tractor.targetApplicationPsi,
  ]);

  // Validation: explicit checks per alert
  const getAlertConfigErrors = () => {
    const errors = [];

    const validateAlert = (alert, label) => {
      if (!alert || !alert.enabled) return;

      if (!alert.triggerAfterMinutes || alert.triggerAfterMinutes === '' || isNaN(Number(alert.triggerAfterMinutes))) {
        errors.push(`${label}: Trigger After (min) is required.`);
      }
      if (!alert.minEnabled && !alert.maxEnabled) {
        errors.push(`${label}: At least one of Min or Max must be enabled.`);
      }
      if (alert.minEnabled && (alert.minPercent === '' || alert.minPercent === undefined)) {
        errors.push(`${label}: Min (%) value is required.`);
      }
      if (alert.maxEnabled && (alert.maxPercent === '' || alert.maxPercent === undefined)) {
        errors.push(`${label}: Max (%) value is required.`);
      }
      if (
        alert.minEnabled && alert.maxEnabled &&
        alert.minPercent !== '' && alert.maxPercent !== '' &&
        !isNaN(Number(alert.minPercent)) && !isNaN(Number(alert.maxPercent)) &&
        Number(alert.minPercent) >= Number(alert.maxPercent)
      ) {
        errors.push(`${label}: Min (%) must be less than Max (%).`);
      }
    };

    validateAlert(alerts.spot, 'Spot Spray Application Rate');
    validateAlert(alerts.broadcast, 'Broadcast Spray Application Rate');
    validateAlert(alerts.pressure, 'Pressure');

    return errors;
  };

  const alertConfigErrors = getAlertConfigErrors();
  const isAlertConfigValid = alertConfigErrors.length === 0;

  // Change detection
  const haveAnyValuesChanged = () => !_.isEqual(alerts, fetchedAlerts);

  // Discard changes
  const discardChanges = () => {
    setAlerts(_.cloneDeep(fetchedAlerts));
  };

  // Save changes (simulate API, replace with real API if available)
  const updateAlerts = async () => {
    const toNumber = (value, fallback = 0) => {
      if (value === '' || value === null || value === undefined) return fallback;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? fallback : parsed;
    };

    // Build payload for backend
    const buildAlertPayload = (type) => {
      const state = alerts[type];
      return {
        Id: state.id || 0,
        TractorId: toNumber(tractorId),
        Enabled: state.enabled,
        TriggerAfterMinutes: toNumber(state.triggerAfterMinutes),
        CooldownMinutes: toNumber(state.cooldownMinutes, 0),
        LastTriggeredUtc: state.lastTriggeredUtc ?? null,
        ConfigJson: JSON.stringify({
          MinThresholdEnabled: state.minEnabled,
          MinThresholdPercent: toNumber(state.minPercent),
          MaxThresholdEnabled: state.maxEnabled,
          MaxThresholdPercent: toNumber(state.maxPercent),
        }),
      };
    };

    const payload = {
      AppRateSpotSprayAlert: buildAlertPayload('spot'),
      AppRateBroadcastAlert: buildAlertPayload('broadcast'),
      PressureAlert: buildAlertPayload('pressure')
    };

    try {
      const token = await authService.getAccessToken();
      // TODO: Replace with real API endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alerts/update?tractorId=${tractorId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error('Failed to update alerts');
      setFetchedAlerts(_.cloneDeep(alerts));
      toast.success('Alerts updated successfully!');
    } catch (error) {
      toast.error('Failed to update alerts');
    }
  };

  // Render alert section
  const renderAlertSection = (type, label) => {
    const state = alerts[type];
    if (!state) return null; // Guard against undefined

    let sectionConfig;
    switch (type) {
      case 'pressure':
        sectionConfig = {
          targetValue: tractor.targetPressurePsi ?? tractor.targetApplicationPsi,
          unit: 'psi',
          targetLabel: 'Target Pressure',
          minValueField: 'minPressurePsi',
          maxValueField: 'maxPressurePsi',
          minValue: state.minPressurePsi,
          maxValue: state.maxPressurePsi,
          metricText: 'average pressure',
          modeText: null,
        };
        break;
      case 'broadcast':
        sectionConfig = {
          targetValue: tractor.defaultTargetApplicationRateGalAcre,
          unit: 'gal/ac',
          targetLabel: 'Target Application Rate',
          minValueField: 'minGalAcre',
          maxValueField: 'maxGalAcre',
          minValue: state.minGalAcre,
          maxValue: state.maxGalAcre,
          metricText: 'average application rate',
          modeText: 'broadcast mode',
        };
        break;
      case 'spot':
      default:
        sectionConfig = {
          targetValue: tractor.defaultTargetApplicationRateGalAcre,
          unit: 'gal/ac',
          targetLabel: 'Target Application Rate',
          minValueField: 'minGalAcre',
          maxValueField: 'maxGalAcre',
          minValue: state.minGalAcre,
          maxValue: state.maxGalAcre,
          metricText: 'average application rate',
          modeText: 'any of the spot spray modes',
        };
        break;
    }

    const {
      targetValue,
      unit,
      targetLabel,
      minValueField,
      maxValueField,
      minValue,
      maxValue,
      metricText,
      modeText,
    } = sectionConfig;

    return (
      <Paper sx={{ p: 3, mb: 4 }}>  
        <Stack spacing={2}>
          {/* Title and master enabled slider in one row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormLabel>Enabled</FormLabel>
              <Switch
                checked={state.enabled}
                onChange={handleToggle(type, 'enabled')}
                color="primary"
              />
            </Stack>
          </Stack>
          {/* Sub-header */}
          <Typography variant="subtitle1">
            {targetLabel}: {targetValue ?? 'N/A'} {unit}
          </Typography>
          {/* Bottom 4 columns */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} justifyContent="center" alignItems={{ xs: 'stretch', sm: 'center' }}>
            {/* Column 1: Trigger After (min) */}
            <Stack alignItems="center" spacing={1} sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="subtitle2">Trigger After (min)</Typography>
              <TextField
                size="small"
                sx={{ width: 100 }}
                type="number"
                value={state.triggerAfterMinutes}
                onChange={handleInput(type, 'triggerAfterMinutes')}
                disabled={!state.enabled}
                min={0}
              />
            </Stack>
            {/* Column 2: Enabled */}
            <Stack alignItems="center" spacing={1} sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="subtitle2">Enabled</Typography>
              <Stack direction="column" spacing={2} alignItems="center">
                <Switch
                  checked={state.minEnabled}
                  onChange={handleToggle(type, 'minEnabled')}
                  color="primary"
                  size="small"
                  disabled={!state.enabled}
                />
                <Switch
                  checked={state.maxEnabled}
                  onChange={handleToggle(type, 'maxEnabled')}
                  color="primary"
                  size="small"
                  disabled={!state.enabled}
                />
              </Stack>
            </Stack>
            {/* Column 3: Set Thresholds by % */}
            <Stack alignItems="center" spacing={1} sx={{ minWidth: 260, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="subtitle2">Set Thresholds by %</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel>Min (%)</FormLabel>
                <TextField
                  name={`${type}-minPercent`}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                  value={state.minPercent}
                  onChange={handleInput(type, 'minPercent')}
                  disabled={!state.enabled || !state.minEnabled}
                  min={0}
                  max={100}
                  step={0.01}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel>Max (%)</FormLabel>
                <TextField
                  name={`${type}-maxPercent`}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                  value={state.maxPercent}
                  onChange={handleInput(type, 'maxPercent')}
                  disabled={!state.enabled || !state.maxEnabled}
                  min={0}
                  max={100}
                  step={0.01}
                />
              </Stack>
            </Stack>
            {/* Column 4: Set Thresholds by value */}
            <Stack alignItems="center" spacing={1} sx={{ minWidth: 260, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="subtitle2">Set Thresholds by {unit}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel>Min ({unit})</FormLabel>
                <TextField
                  name={`${type}-${minValueField}`}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                  value={minValue}
                  onChange={handleInput(type, minValueField)}
                  disabled={!state.enabled || !state.minEnabled}
                  min={0}
                  step={0.01}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel>Max ({unit})</FormLabel>
                <TextField
                  name={`${type}-${maxValueField}`}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                  value={maxValue}
                  onChange={handleInput(type, maxValueField)}
                  disabled={!state.enabled || !state.maxEnabled}
                  min={0}
                  step={0.01}
                />
              </Stack>
            </Stack>
          </Stack>
          {/* Dynamic explanation row */}
          {(() => {
            if (!state.enabled) {
              return (
                <Typography variant="body2" sx={{ mt: 2, color: '#1976d2' }}>
                  *This alert will not be triggered.*
                </Typography>
              );
            }
            const minEnabled = state.minEnabled;
            const maxEnabled = state.maxEnabled;
            // Hide explanation if enabled and both min/max are disabled
            if (state.enabled && !minEnabled && !maxEnabled) {
              return null;
            }
            const triggerAfterMinutes = state.triggerAfterMinutes;
            let parts = [];
            if (minEnabled) {
              parts.push(<span key="min">drops below <strong>{minValue}</strong> {unit}</span>);
            }
            if (maxEnabled) {
              parts.push(<span key="max">goes above <strong>{maxValue}</strong> {unit}</span>);
            }
            const thresholdMsg = parts.length
              ? <span>Triggered if the {metricText} {parts.reduce((prev, curr) => [prev, ' or ', curr])}</span>
              : 'Triggered if the alert is enabled';

            if (!modeText) {
              return (
                <Typography variant="body2" sx={{ mt: 2, color: '#1976d2' }}>
                  *{thresholdMsg} for <strong>{triggerAfterMinutes}</strong> minutes.
                </Typography>
              );
            }

            return (
              <Typography variant="body2" sx={{ mt: 2, color: '#1976d2' }}>
                *{thresholdMsg} for <strong>{triggerAfterMinutes}</strong> minutes, while in {modeText}.
              </Typography>
            );
          })()}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : !tractor.defaultTargetApplicationRateGalAcre ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
          <Typography color="error" variant="h6">
            A default Target Application Rate (gal/ac) is required for this tractor to configure alerts.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
            {tractor.name}
          </Typography>
          <Typography variant="h5" sx={{ pb: 2 }}>
            Real Time Alert Configuration
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <Box />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                onClick={discardChanges}
                disabled={!haveAnyValuesChanged()}
                startIcon={<UndoIcon />}
              >
                Discard Changes
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={updateAlerts}
                disabled={!haveAnyValuesChanged() || !isAlertConfigValid}
                startIcon={<SaveIcon />}
              >
                Save Changes
              </Button>
            </Stack>
          </Stack>
          {!isAlertConfigValid && (
            <Stack sx={{ pb: 2 }}>
              {alertConfigErrors.map((err, idx) => (
                <Typography color="error" key={idx}>{err}</Typography>
              ))}
            </Stack>
          )}
          {renderAlertSection('spot', 'Spot Spray Application Rate')}
          {renderAlertSection('broadcast', 'Broadcast Spray Application Rate')}
          {renderAlertSection('pressure', 'Pressure')}
        </>
      )}
    </Box>
  );
};

export default AdminConfigAlerts;